/**
 * Crop editor helpers — preview/recrop UI only (does not affect PDF export quality).
 */

export const FULL_PAGE_CROP = { x: 0, y: 0, width: 100, height: 100 };

export const MIN_CROP_WIDTH_PERCENT = 35;
export const MIN_CROP_HEIGHT_PERCENT = 25;

/** Minimum crop size enforced during live drag when shrinking inward. */
export const MIN_DRAG_CROP_WIDTH_PERCENT = MIN_CROP_WIDTH_PERCENT;
export const MIN_DRAG_CROP_HEIGHT_PERCENT = MIN_CROP_HEIGHT_PERCENT;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/** Normalize rotation to 0–359 integer degrees. */
export function normalizeRotationDegrees(rotation) {
  return ((Math.round(rotation) % 360) + 360) % 360;
}

/** True when 90° or 270° — displayed width/height swap vs unrotated layout. */
export function shouldSwapStageDimensions(rotationDegrees) {
  const normalized = normalizeRotationDegrees(rotationDegrees);
  return normalized === 90 || normalized === 270;
}

/** Effective natural pixel dimensions after orthogonal rotation. */
export function getEffectiveNaturalDimensions(naturalWidth, naturalHeight, rotationDegrees) {
  if (shouldSwapStageDimensions(rotationDegrees)) {
    return { effectiveWidth: naturalHeight, effectiveHeight: naturalWidth };
  }
  return { effectiveWidth: naturalWidth, effectiveHeight: naturalHeight };
}

/** Stage pixel size that matches the rotated visual bounds. */
export function getStageDimensions(baseWidth, baseHeight, rotationDegrees) {
  if (!baseWidth || !baseHeight) {
    return { stageWidth: baseWidth, stageHeight: baseHeight };
  }
  if (shouldSwapStageDimensions(rotationDegrees)) {
    return { stageWidth: baseHeight, stageHeight: baseWidth };
  }
  return { stageWidth: baseWidth, stageHeight: baseHeight };
}

/** Reference page inches for dimension fields — swaps at 90°/270°. */
export function getReferencePageInches(
  rotationDegrees,
  { widthInches = 8.5, heightInches = 11 } = {},
) {
  if (shouldSwapStageDimensions(rotationDegrees)) {
    return { pageWidthInches: heightInches, pageHeightInches: widthInches };
  }
  return { pageWidthInches: widthInches, pageHeightInches: heightInches };
}

/** Fit natural image into max display box (unrotated). */
export function computeFittedDisplaySize(naturalWidth, naturalHeight, maxWidth, maxHeight) {
  if (!naturalWidth || !naturalHeight || !maxWidth || !maxHeight) {
    return { width: 0, height: 0 };
  }
  const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1);
  return {
    width: naturalWidth * scale,
    height: naturalHeight * scale,
  };
}

/** Convert viewport pointer position to image-local percentage (0–100). */
export function pointerToImagePercent(clientX, clientY, imageRect) {
  const width = imageRect?.width || 0;
  const height = imageRect?.height || 0;
  if (!width || !height) {
    return { x: 0, y: 0 };
  }
  return {
    x: clamp(((clientX - imageRect.left) / width) * 100, 0, 100),
    y: clamp(((clientY - imageRect.top) / height) * 100, 0, 100),
  };
}

/**
 * Apply crop resize using absolute pointer position against the full displayed image rect.
 * Clamps only to full image bounds (0–100), never to a previous crop region.
 */
export function applyCropResizeFromPointer(
  area,
  handle,
  clientX,
  clientY,
  imageRect,
  {
    minWidth = MIN_DRAG_CROP_WIDTH_PERCENT,
    minHeight = MIN_DRAG_CROP_HEIGHT_PERCENT,
  } = {},
) {
  const { x: px, y: py } = pointerToImagePercent(clientX, clientY, imageRect);
  let { x, y, width, height } = area;

  switch (handle) {
    case 'e': {
      const rightPercent = clamp(px, x + minWidth, 100);
      width = rightPercent - x;
      break;
    }
    case 'w': {
      const right = x + width;
      const leftPercent = clamp(px, 0, right - minWidth);
      x = leftPercent;
      width = right - leftPercent;
      break;
    }
    case 'n': {
      const bottom = y + height;
      const topPercent = clamp(py, 0, bottom - minHeight);
      y = topPercent;
      height = bottom - topPercent;
      break;
    }
    case 's': {
      const bottomPercent = clamp(py, y + minHeight, 100);
      height = bottomPercent - y;
      break;
    }
    case 'nw': {
      const right = x + width;
      const bottom = y + height;
      const leftPercent = clamp(px, 0, right - minWidth);
      const topPercent = clamp(py, 0, bottom - minHeight);
      x = leftPercent;
      y = topPercent;
      width = right - leftPercent;
      height = bottom - topPercent;
      break;
    }
    case 'ne': {
      const bottom = y + height;
      const rightPercent = clamp(px, x + minWidth, 100);
      const topPercent = clamp(py, 0, bottom - minHeight);
      width = rightPercent - x;
      y = topPercent;
      height = bottom - topPercent;
      break;
    }
    case 'sw': {
      const right = x + width;
      const leftPercent = clamp(px, 0, right - minWidth);
      const bottomPercent = clamp(py, y + minHeight, 100);
      x = leftPercent;
      width = right - leftPercent;
      height = bottomPercent - y;
      break;
    }
    case 'se': {
      const rightPercent = clamp(px, x + minWidth, 100);
      const bottomPercent = clamp(py, y + minHeight, 100);
      width = rightPercent - x;
      height = bottomPercent - y;
      break;
    }
    default:
      break;
  }

  return { x, y, width, height };
}

/**
 * Apply a crop move drag delta in image-local percentage coordinates.
 */
export function applyCropResize(
  area,
  handle,
  deltaXPercent,
  deltaYPercent,
  {
    minWidth = MIN_DRAG_CROP_WIDTH_PERCENT,
    minHeight = MIN_DRAG_CROP_HEIGHT_PERCENT,
  } = {},
) {
  if (handle !== 'move') {
    return area;
  }

  let { x, y, width, height } = area;
  x = clamp(x + deltaXPercent, 0, 100 - width);
  y = clamp(y + deltaYPercent, 0, 100 - height);
  return { x, y, width, height };
}

/** Four dimming panels for areas outside the crop box (percent of full image). */
export function buildCropOverlayPanels(crop) {
  const { x, y, width, height } = crop;
  if (x === 0 && y === 0 && width === 100 && height === 100) {
    return [];
  }

  const bottom = y + height;
  const right = x + width;

  return [
    { key: 'top', left: 0, top: 0, width: 100, height: y },
    { key: 'bottom', left: 0, top: bottom, width: 100, height: 100 - bottom },
    { key: 'left', left: 0, top: y, width: x, height },
    { key: 'right', left: right, top: y, width: 100 - right, height },
  ];
}

/** Convert saved crop metadata to percentage crop area for the editor. */
export function cropAreaFromMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return null;

  const percent = metadata.crop_box_percent;
  if (percent && typeof percent === 'object') {
    return {
      x: (percent.x ?? 0) * 100,
      y: (percent.y ?? 0) * 100,
      width: (percent.width ?? 1) * 100,
      height: (percent.height ?? 1) * 100,
    };
  }

  const saved = metadata.saved_crop;
  const naturalWidth = metadata.natural_image_width;
  const naturalHeight = metadata.natural_image_height;
  if (saved && naturalWidth > 0 && naturalHeight > 0) {
    return {
      x: (saved.x / naturalWidth) * 100,
      y: (saved.y / naturalHeight) * 100,
      width: (saved.width / naturalWidth) * 100,
      height: (saved.height / naturalHeight) * 100,
    };
  }

  if (typeof metadata.x === 'number' && typeof metadata.width === 'number') {
    return {
      x: metadata.x,
      y: metadata.y ?? 0,
      width: metadata.width,
      height: metadata.height ?? 100,
    };
  }

  return null;
}

/**
 * Clamp crop area inside image bounds and enforce minimum size on editor open.
 * Does not run during live drag — only when restoring a saved/recrop state.
 */
export function normalizeCropArea(
  area,
  {
    minWidth = MIN_CROP_WIDTH_PERCENT,
    minHeight = MIN_CROP_HEIGHT_PERCENT,
    enforceMinimum = true,
  } = {},
) {
  if (!area) return { ...FULL_PAGE_CROP };

  let { x, y, width, height } = area;
  width = Math.max(1, Math.min(100, width));
  height = Math.max(1, Math.min(100, height));

  if (enforceMinimum) {
    if (width < minWidth) width = minWidth;
    if (height < minHeight) height = minHeight;
  }

  x = Math.max(0, Math.min(100 - width, x));
  y = Math.max(0, Math.min(100 - height, y));

  return { x, y, width, height };
}

export function cropSettingsFromMetadata(metadata) {
  if (!metadata) {
    return { cropArea: { ...FULL_PAGE_CROP }, rotation: 0, straighten: 0 };
  }
  const cropArea = normalizeCropArea(cropAreaFromMetadata(metadata));
  const totalRotation = metadata.rotation_degrees ?? 0;
  const straighten = metadata.straighten_degrees ?? 0;
  return {
    cropArea,
    rotation: totalRotation - straighten,
    straighten,
  };
}

export function buildApplyCropSettingsFromMetadata(metadata) {
  const cp = metadata?.crop_box_percent;
  if (!cp) return null;
  return {
    x: (cp.x ?? 0) * 100,
    y: (cp.y ?? 0) * 100,
    width: (cp.width ?? 1) * 100,
    height: (cp.height ?? 1) * 100,
    rotation: metadata.rotation_degrees ?? 0,
    brightness: 1,
    contrast: 1,
    sharpness: 1,
  };
}
